// @ts-check

/**
 * CUSTOM: Premium Smykkeæske som default.
 * Markup/config: snippets/custom-gift-box-default.liquid · kurv-linje: snippets/custom-gift-box-line.liquid
 *
 * Regler (ren logik i decide() – kan testes isoleret: window.CustomGiftBoxDefault.decide):
 * - Kurven har mindst ét "rigtigt" produkt og ingen æske → læg 1 æske i kurven.
 * - Kurven har æske(r), men ingen rigtige produkter → fjern æsken.
 * - Ellers: gør intet (ingen dubletter – æsken tilføjes aldrig, hvis den allerede ligger der).
 * Kunden kan ikke fjerne æsken (ingen Fjern-knap, min. 1), men kan købe flere med +.
 * Prisen (1 stk gratis) styres af Shopify-rabatten, ikke af temaet. Shopify deler da æsken i flere
 * kurv-linjer (1 gratis + betalte); kurven viser dem som én linje, og +/− her styrer det samlede antal.
 *
 * Kører: ved sideindlæsning, efter Motions "ajaxProduct:added" og efter "cart:updated".
 * Kald køres ét ad gangen, og kurven hentes frisk før hver beslutning (reload/flere klik giver ikke dubletter).
 */
(function () {
  /** @type {any} */
  const win = window;
  if (win.CustomGiftBoxDefault) return;

  /**
   * @typedef {{ key: string, variant_id: number, quantity: number, final_line_price: number, requires_shipping: boolean, gift_card: boolean }} CartItem
   * @typedef {{ items: CartItem[] }} Cart
   * @typedef {{ action: 'add' } | { action: 'remove', keys: string[] } | { action: 'none' }} Decision
   */

  /**
   * Ren logik (ingen DOM/netværk).
   * @param {Cart} cart
   * @param {number} giftVariantId
   * @returns {Decision}
   */
  function decide(cart, giftVariantId) {
    const items = (cart && cart.items) || [];
    const giftLines = items.filter((i) => Number(i.variant_id) === Number(giftVariantId));
    const hasRealProduct = items.some(
      (i) => Number(i.variant_id) !== Number(giftVariantId) && i.requires_shipping && !i.gift_card
    );
    if (hasRealProduct && !giftLines.length) return { action: 'add' };
    if (!hasRealProduct && giftLines.length) return { action: 'remove', keys: giftLines.map((i) => i.key) };
    return { action: 'none' };
  }

  /**
   * Ren logik: hvilken æske-linje skal "−" trække fra? Helst en betalt linje, så den gratis bliver.
   * Returnerer null, hvis der kun er 1 æske i alt (må ikke under 1).
   * @param {Cart} cart
   * @param {number} giftVariantId
   * @returns {{ key: string, quantity: number } | null}
   */
  function lineToReduce(cart, giftVariantId) {
    const giftLines = ((cart && cart.items) || []).filter((i) => Number(i.variant_id) === Number(giftVariantId));
    const total = giftLines.reduce((sum, i) => sum + i.quantity, 0);
    if (total <= 1) return null;
    const paid = giftLines.find((i) => i.final_line_price > 0);
    const line = paid || giftLines.reduce((a, b) => (b.quantity > a.quantity ? b : a));
    return { key: line.key, quantity: line.quantity - 1 };
  }

  win.CustomGiftBoxDefault = { decide, lineToReduce };

  const configEl = document.querySelector('[data-custom-gift-box-default]');
  if (!configEl) return;
  /** @type {{ variantId: number }} */
  let config;
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch (e) {
    return;
  }
  if (!config.variantId) return;

  const root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  // Hvis Shopify afviser tilføjelsen (fx udsolgt), prøver vi ikke igen på denne side.
  let addFailed = false;
  /** @type {Promise<void>} */
  let queue = Promise.resolve();

  /** @returns {Promise<Cart>} */
  function getCart() {
    return fetch(root + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json());
  }

  /** Opdater kurv-skuffe, /cart og fri fragt-bar (Motions egne events). */
  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    getCart()
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, customGiftBoxDefault: true } }));
      })
      .catch(() => {});
  }

  /** @returns {Promise<void>} */
  function run() {
    return getCart().then((cart) => {
      const decision = decide(cart, config.variantId);
      if (decision.action === 'add' && !addFailed) {
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: headers,
          body: JSON.stringify({ items: [{ id: config.variantId, quantity: 1 }] }),
        }).then((r) => {
          if (!r.ok) {
            addFailed = true;
            return;
          }
          refreshCartUI();
        });
      }
      if (decision.action === 'remove') {
        /** @type {Record<string, number>} */
        const updates = {};
        decision.keys.forEach((key) => (updates[key] = 0));
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: headers,
          body: JSON.stringify({ updates: updates }),
        }).then((r) => {
          if (r.ok) refreshCartUI();
        });
      }
    });
  }

  function check() {
    queue = queue.then(run).catch(() => {});
  }

  /** "+" på æske-linjen: læg 1 æske mere i kurven (Shopify fordeler gratis/betalt). */
  function plus() {
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: JSON.stringify({ items: [{ id: config.variantId, quantity: 1 }] }),
    }).then(refreshCartUI);
  }

  /** "−" på æske-linjen: træk 1 fra en betalt linje (aldrig under 1 i alt). */
  function minus() {
    return getCart().then((cart) => {
      const target = lineToReduce(cart, config.variantId);
      if (!target) return;
      return fetch(root + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers,
        body: JSON.stringify({ id: target.key, quantity: target.quantity }),
      }).then(refreshCartUI);
    });
  }

  // Capture-fase: vi tager klikket, før Motions QtySelector (bundet direkte på knapperne) får det.
  document.addEventListener(
    'click',
    (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      if (!target || !target.closest) return;
      const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-gift-box-plus], [data-gift-box-minus]'));
      if (!button) return;
      e.preventDefault();
      e.stopPropagation();
      if (button.disabled) return;
      const wrapper = button.closest('.js-qty__wrapper');
      if (wrapper) wrapper.classList.add('is-loading');
      const action = button.hasAttribute('data-gift-box-plus') ? plus : minus;
      queue = queue.then(action).catch(() => {});
    },
    true
  );

  document.addEventListener('ajaxProduct:added', check);
  document.addEventListener('cart:updated', (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail;
    if (detail && detail.customGiftBoxDefault) return; // vores egen opdatering
    check();
  });
  check();
})();
